// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { newestPerTarget } from "@/queries/Lifecycle";
/** The newest publication (of `publications`, newest first) that covers an election: its own, or the event's. */
export var publicationFor = function (publications, electionId) {
    var _a;
    return (_a = publications.find(function (publication) { return !publication.election_id || publication.election_id === electionId; })) !== null && _a !== void 0 ? _a : null;
};
var sameInstant = function (a, b) {
    return (a ? Date.parse(a) : NaN) === (b ? Date.parse(b) : NaN) || (!a && !b);
};
var sameChannels = function (a, b) {
    return __spreadArray([], (a !== null && a !== void 0 ? a : []), true).sort().join("|") === __spreadArray([], (b !== null && b !== void 0 ? b : []), true).sort().join("|");
};
/** Whether a row differs from what a publication recorded of it. */
var differs = function (event, published) {
    var _a, _b, _c, _d, _e, _f;
    if (!published)
        return true;
    var cron = ((_a = event.cron_config) !== null && _a !== void 0 ? _a : {});
    var payload = ((_b = event.event_payload) !== null && _b !== void 0 ? _b : {});
    return (!sameInstant(cron.scheduled_date, published.scheduled_date) ||
        ((_c = cron.local) !== null && _c !== void 0 ? _c : null) !== ((_d = published.local) !== null && _d !== void 0 ? _d : null) ||
        ((_e = cron.timezone) !== null && _e !== void 0 ? _e : null) !== ((_f = published.timezone) !== null && _f !== void 0 ? _f : null) ||
        !sameChannels(payload.voting_channels, published.voting_channels));
};
/** Whether voters of an election see this row as it is (after `publication`). */
var seenAsIs = function (event, publication) {
    var _a;
    if (!publication)
        return false;
    var schedule = (_a = publication.snapshot) === null || _a === void 0 ? void 0 : _a.schedule;
    var voting = event.event_processor === "START_VOTING_PERIOD" ||
        event.event_processor === "END_VOTING_PERIOD";
    if (voting && schedule) {
        return !differs(event, schedule.find(function (_a) {
            var scheduled_event_id = _a.scheduled_event_id;
            return scheduled_event_id === event.id;
        }));
    }
    return Date.parse(event.created_at) <= Date.parse(publication.published_at);
};
/**
 * The ids of rows voters don't see yet. A publication's lifecycle snapshot
 * lists its openings and closings, so those are compared field by field;
 * other rows count as changed when created after the publication. An
 * event-wide row is seen only when every election's covering publication has
 * it as it is. Rows that already ran, and every row before the first
 * publication, aren't marked.
 */
export var unpublishedEventIds = function (events, publications, electionIds) {
    var _a;
    if (electionIds === void 0) { electionIds = []; }
    var ids = new Set();
    if (!publications.length)
        return ids;
    var newest = newestPerTarget(publications);
    var _loop_1 = function (event_1) {
        if (event_1.stopped_at)
            return "continue";
        var electionId = (_a = event_1.event_payload) === null || _a === void 0 ? void 0 : _a.election_id;
        var targets = electionId ? [electionId] : electionIds.length ? electionIds : [null];
        var seen = targets.every(function (target) { var _a; return seenAsIs(event_1, target ? publicationFor(newest, target) : ((_a = newest[0]) !== null && _a !== void 0 ? _a : null)); });
        if (!seen)
            ids.add(String(event_1.id));
    };
    for (var _i = 0, events_1 = events; _i < events_1.length; _i++) {
        var event_1 = events_1[_i];
        _loop_1(event_1);
    }
    return ids;
};
